import { useState } from 'react'

import { errorMessage } from './hooks'

/**
 * Runs a write and keeps its refusal to show: `run` resolves true when the
 * write succeeded, and `message` is the alert to render when it did not.
 */
export function useRunner() {
  const [error, setError] = useState<string | null>(null)
  const run = async (action: () => Promise<unknown>, fallback: string): Promise<boolean> => {
    setError(null)
    try {
      await action()
      return true
    } catch (caught) {
      setError(errorMessage(caught, fallback))
      return false
    }
  }
  const message = error ? (
    <p role="alert" className="camp-error">
      {error}
    </p>
  ) : null
  return { run, message }
}

/**
 * A card section that toggles between showing a value and editing a copy of
 * it. Opening or cancelling starts again from `current`; a successful save
 * closes the editor, a refused one keeps it open with the reason.
 */
export function useEditableSection<T>(current: T) {
  const { run, message } = useRunner()
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState<T>(current)

  const toggle = () => {
    setDraft(current)
    setEditing((value) => !value)
  }

  const save = async (action: () => Promise<unknown>, fallback: string) => {
    if (await run(action, fallback)) setEditing(false)
  }

  return { editing, toggle, draft, setDraft, save, message }
}
